// StatusSelect (DESIGN §3.9.12): Status als farbiger Text mit Chevron („On track ˅“), öffnet ein
// Select mit Punkt + Text. Ohne Recht reiner Text in gleicher Optik ohne Chevron. Speichern, Toast
// und Undo macht der Aufrufer (onValueChange).
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { Select as SelectPrimitive } from 'radix-ui';
import { menuItem } from './_lib/menuStyles';
import { selectContentClass } from './Select';
import './_lib/motion.css';
import { cn } from '../basis/kern';

export type StatusTone = 'success' | 'warning' | 'danger' | 'accent' | 'neutral';

export interface StatusOption {
  value: string;
  label: string;
  tone: StatusTone;
}

/** Zielstatus (Goals): On track · At risk · Delayed. */
export const GOAL_STATUS_OPTIONS: StatusOption[] = [
  { value: 'on_track', label: 'On track', tone: 'success' },
  { value: 'at_risk', label: 'At risk', tone: 'warning' },
  { value: 'delayed', label: 'Delayed', tone: 'danger' },
];

const textTone: Record<StatusTone, string> = {
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
  accent: 'text-accent-fg',
  neutral: 'text-fg-secondary',
};
const dotTone: Record<StatusTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  accent: 'bg-accent',
  neutral: 'bg-fg-tertiary',
};

export interface StatusSelectProps {
  value: string;
  onValueChange?: (value: string) => void;
  options?: StatusOption[];
  /** Ohne Recht: nur Text. */
  readOnly?: boolean;
  disabled?: boolean;
  /** Name des Auslösers, z. B. „Status of Dream Team“. */
  'aria-label'?: string;
  className?: string;
}

export function StatusSelect({
  value,
  onValueChange,
  options = GOAL_STATUS_OPTIONS,
  readOnly = false,
  disabled,
  className,
  ...aria
}: StatusSelectProps) {
  const current = options.find((o) => o.value === value);
  const tone = current?.tone ?? 'neutral';
  const text = current?.label ?? value;
  if (readOnly) {
    return <span className={cn('text-body', textTone[tone], className)}>{text}</span>;
  }
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={aria['aria-label'] ? `${aria['aria-label']}: ${text}` : undefined}
        className={cn(
          'inline-flex h-8 items-center gap-1 rounded-full px-2 -mx-2 text-body whitespace-nowrap',
          'transition-colors duration-(--bar-dur-fast) ease-standard hover:not-disabled:bg-pill-stripe',
          'disabled:cursor-not-allowed disabled:opacity-60',
          textTone[tone],
          className,
        )}
      >
        <SelectPrimitive.Value>{text}</SelectPrimitive.Value>
        <SelectPrimitive.Icon className="inline-flex">
          <CaretDownIcon size={14} aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={8}
          collisionPadding={16}
          className={cn(selectContentClass, 'min-w-44')}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className={cn(menuItem, 'min-h-10 pr-9')}
              >
                <span
                  aria-hidden="true"
                  className={cn('size-2 shrink-0 rounded-full', dotTone[option.tone])}
                />
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-3 inline-flex text-accent-fg">
                  <CheckIcon size={16} weight="bold" aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
